import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import fetchAPI from '../fetchAPI';

/**
 * A company's own details, read and written against the company itself.
 *
 * The account page used to reach these only through the company's first user
 * (`callGetUser` → `user.company`, saved by `callUpdateUser`). That leaves a
 * company created from the superadmin with no users — every Street account,
 * until its first sync — with nothing to show and nothing to save against.
 */
export interface CompanyDetailsValues {
  name: string;
  website: string;
  address: string;
}

const COMPANY_KEY = 'company';

export const useCompanyDetails = (companyId?: string) =>
  useQuery({
    queryKey: [COMPANY_KEY, companyId, 'details'],
    enabled: Boolean(companyId),
    queryFn: async () => {
      // Same nesting as the billing-model read: the payload sits one level
      // under handleGET's `data`.
      const response: iResponse<{ data?: Partial<CompanyDetailsValues> }> = await fetchAPI({
        method: 'GET',
        route: `company/${companyId}`
      });
      if (response.code >= 400) {
        throw new Error(response.error?.message ?? 'Could not load the company.');
      }
      const data = response.data?.data ?? {};
      return {
        name: data.name ?? '',
        website: data.website ?? '',
        address: data.address ?? ''
      } as CompanyDetailsValues;
    }
  });

export const useUpdateCompanyDetails = (companyId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: CompanyDetailsValues) => {
      // Blank optionals go as null, not "": Companies.website carries @IsUrl,
      // and Sequelize validates "" while skipping null.
      const response: iResponse<unknown> = await fetchAPI({
        method: 'PATCH',
        route: `company/${companyId}`,
        data: {
          name: values.name.trim(),
          website: values.website.trim() || null,
          address: values.address.trim() || null
        } as APIData
      });
      // fetchAPI hands 4xx back as data rather than throwing.
      if (response.code >= 400) {
        throw new Error(response.error?.message ?? 'Could not save the company.');
      }
      return values;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMPANY_KEY, companyId] });
    }
  });
};
